import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Coachee, Method } from "../../models";
import { ExtendedClientDetails as ExtendedClientDetailsEntity } from "../../models/coachingNavigator/extendedClientDetails";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import { MethodService } from "../../services/coachingNavigator/methodService";
import { ExtendedClientDetailsService } from "../../services/coachingNavigator/extendedClientDetailsService";
import config from "../../config";
import ConfirmationPanel from "../../components/confirmationPanel";
import {
  KeyInformationSection,
  ClientObjectivesSection,
  FurtherRemarksSection,
  MethodsSection,
  PersonalInformationSection,
  CoachingPackageSection,
  AdminSection,
} from "./clientdetails";
import type {
  Remark,
  ClientObjective,
  CoachingPackage,
  Invoice,
  UserData,
} from "./clientdetails";
import SkeletonLoader from "../../components/skeletonLoader";
import { exportClientToPDF } from "../../util/pdfExportUtil";
import { Context } from "../../components/context";

const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");
const methodService = new MethodService(config.api.baseUrl, "/method");
const extendedDetailsService = new ExtendedClientDetailsService(
  config.api.baseUrl,
  "/extendedClientDetails",
);

const emptyUserData: UserData = {
  name: "",
  company: "",
  role: "",
  email: "",
  linkedIn: "",
  dateOfBirth: null as unknown as Date,
  picture: "",
  wohnort: "",
  arbeitsort: "",
  arbeitsortOverridden: false,
  completedHours: 0,
  coachingPackages: [],
  clientObjectives: [],
  remarks: [],
  personalAttributes: {},
  methodsUsed: [],
  invoices: [],
};

const toDateOrNull = (value?: Date | string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

function ClientOverview() {
  const { coacheeId } = useParams<{ coacheeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appContext = useContext(Context);
  const creatorEmail = useMemo(
    () => (appContext.state.invoker?.email || "").trim(),
    [appContext.state.invoker?.email],
  );
  const [userData, setUserData] = useState<UserData>(emptyUserData);
  const [availableAttributes, setAvailableAttributes] = useState<string[]>([]);
  const [availableMethods, setAvailableMethods] = useState<Method[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persistUpdate = useCallback(
    async (override?: Partial<UserData>) => {
      if (!coacheeId) return;
      const merged = { ...userData, ...override } as UserData;
      const homeLocation = (merged.wohnort ?? "").trim();
      const workLocation = (merged.arbeitsort ?? "").trim();
      const payload: Coachee = {
        id: coacheeId,
        name: merged.name,
        company: merged.company || undefined,
        role: merged.role || undefined,
        email: merged.email,
        linkedIn: merged.linkedIn || undefined,
        homeLocation,
        workLocation,
        birthday: merged.dateOfBirth,
        image: merged.picture || undefined,
        keyMotivation: merged.clientObjectives.map((o) => ({
          text: o.content,
          date: o.timestamp,
        })),
        remarks: merged.remarks.map((r) => ({
          text: r.content,
          date: r.timestamp,
        })),
        packages: merged.coachingPackages.map((p) => ({
          hoursBooked: p.hoursBooked,
          startDate: p.startDate,
          endDate: p.endDate,
          comment: p.comment,
        })),
        invoices: merged.invoices.map((inv) => ({
          dateSent: inv.dateSent,
          amount: inv.amount,
          remarks: inv.remarks,
          paid: inv.isPaid,
        })),
        bookedHours: merged.coachingPackages.reduce(
          (sum, p) => sum + (p.hoursBooked || 0),
          0,
        ),
        completedHours: merged.completedHours,
        methodsUsed: merged.methodsUsed,
        extendedClientDetails: Object.entries(merged.personalAttributes)
          .filter(([, value]) => value === true || value === false)
          .map(([key, value]) => ({ details: key, true: value as boolean })),
      };
      try {
        await coacheeService.save(payload);
      } catch (err) {
        console.error("Failed to save coachee update", err);
        setError(t("coachingNavigator.clientOverview.failedToSave"));
      }
    },
    [coacheeId, userData],
  );

  const mapCoacheeToUserData = useCallback((coachee: Coachee): UserData => {
    const dob = toDateOrNull(coachee.birthday);
    const created = toDateOrNull(coachee.createdDate) ?? new Date();
    const arbeitsortOverridden = Boolean(
      coachee.workLocation && coachee.workLocation !== coachee.homeLocation,
    );

    const completedHours = Number(coachee.completedHours ?? 0) || 0;

    const packages: CoachingPackage[] = (coachee.packages || []).map(
      (pkg, idx) => ({
        id: pkg.comment || pkg.startDate?.toString() || `pkg-${idx}`,
        startDate: toDateOrNull(pkg.startDate) ?? new Date(),
        endDate: toDateOrNull(pkg.endDate) ?? new Date(),
        hoursBooked: pkg.hoursBooked ?? 0,
        comment: pkg.comment,
      }),
    );

    const invoices: Invoice[] = (coachee.invoices || []).map((inv, idx) => ({
      id: `inv-${idx}`,
      dateSent: toDateOrNull(inv.dateSent) ?? new Date(),
      amount: inv.amount ?? 0,
      isPaid: !!inv.paid,
      remarks: inv.remarks,
    }));

    const objectives: ClientObjective[] = Array.isArray(coachee.keyMotivation)
      ? coachee.keyMotivation.map((item, idx) => ({
          id: `motivation-${idx}`,
          content: (item as any).text ?? (item as any),
          timestamp: toDateOrNull((item as any).date) ?? created,
        }))
      : coachee.keyMotivation
        ? [
            {
              id: "motivation",
              content:
                (coachee.keyMotivation as any).text ??
                (coachee.keyMotivation as any),
              timestamp:
                toDateOrNull((coachee.keyMotivation as any).date) ?? created,
            },
          ]
        : [];

    const remarks: Remark[] = Array.isArray(coachee.remarks)
      ? coachee.remarks.map((item, idx) => ({
          id: `remark-${idx}`,
          content: (item as any).text ?? (item as any),
          timestamp: toDateOrNull((item as any).date) ?? created,
        }))
      : [];

    const personalAttributes: Record<string, boolean | null> = {};
    if (Array.isArray(coachee.extendedClientDetails)) {
      coachee.extendedClientDetails.forEach((item) => {
        if (item && item.details) {
          personalAttributes[item.details] =
            item.true === true ? true : item.true === false ? false : null;
        }
      });
    }

    return {
      ...emptyUserData,
      name: coachee.name || "",
      company: coachee.company || "",
      role: coachee.role || "",
      email: coachee.email || "",
      linkedIn: coachee.linkedIn || "",
      dateOfBirth: dob,
      picture: coachee.image || "",
      wohnort: coachee.homeLocation || "",
      arbeitsort: coachee.workLocation || coachee.homeLocation || "",
      arbeitsortOverridden,
      completedHours,
      coachingPackages: packages,
      clientObjectives: objectives,
      remarks,
      invoices,
      methodsUsed: coachee.methodsUsed || [],
      personalAttributes,
    };
  }, []);

  useEffect(() => {
    if (!coacheeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await coacheeService.recalculateCompletedHours(coacheeId);
        const data = await coacheeService.get(coacheeId);
        if (!cancelled && data) {
          setUserData(mapCoacheeToUserData(data));
        }
      } catch (err) {
        console.error("Failed to load coachee", err);
        if (!cancelled)
          setError(t("coachingNavigator.clientOverview.failedToLoad"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [coacheeId, mapCoacheeToUserData]);

  useEffect(() => {
    let cancelled = false;
    const loadMethods = async () => {
      try {
        if (!creatorEmail) {
          if (!cancelled) setAvailableMethods([]);
          return;
        }
        const list = await methodService.getByCreator(creatorEmail);
        if (!cancelled) setAvailableMethods(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load methods", err);
        if (!cancelled)
          setError(t("coachingNavigator.clientOverview.failedToLoadMethods"));
      }
    };
    loadMethods();
    return () => {
      cancelled = true;
    };
  }, [creatorEmail]);

  useEffect(() => {
    let cancelled = false;
    const loadExtendedDetails = async () => {
      try {
        if (!creatorEmail) {
          if (!cancelled) setAvailableAttributes([]);
          return;
        }
        const list = await extendedDetailsService.getByCreator(creatorEmail);
        const details = (Array.isArray(list) ? list : [])
          .map((item: ExtendedClientDetailsEntity) => item.details)
          .filter(Boolean);
        const uniqueDetails = Array.from(new Set(details));
        if (!cancelled) {
          setAvailableAttributes(uniqueDetails);
          setUserData((prev) => {
            const mergedAttrs: Record<string, boolean | null> = {
              ...prev.personalAttributes,
            };
            uniqueDetails.forEach((attr) => {
              if (mergedAttrs[attr] === undefined) mergedAttrs[attr] = null;
            });
            return { ...prev, personalAttributes: mergedAttrs };
          });
        }
      } catch (err) {
        console.error("Failed to load personal attributes", err);
        if (!cancelled)
          setError(
            t("coachingNavigator.clientOverview.failedToLoadAttributes"),
          );
      }
    };
    loadExtendedDetails();
    return () => {
      cancelled = true;
    };
  }, [creatorEmail]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeConfirmName, setPurgeConfirmName] = useState("");
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const [newAttribute, setNewAttribute] = useState("");
  const [editingAttribute, setEditingAttribute] = useState<string | null>(null);
  const [editAttributeValue, setEditAttributeValue] = useState("");

  const handleKeyInfoUpdate = (field: keyof UserData, value: string | Date) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleWohnortChange = (value: string) => {
    setUserData((prev) => ({
      ...prev,
      wohnort: value,
      arbeitsort: prev.arbeitsortOverridden ? prev.arbeitsort : value,
    }));
  };

  const handleArbeitsortChange = (value: string) => {
    setUserData((prev) => ({
      ...prev,
      arbeitsort: value,
      arbeitsortOverridden: true,
    }));
  };

  const handleAddRemark = (content: string) => {
    const remark: Remark = {
      id: Date.now().toString(),
      content,
      timestamp: new Date(),
    };
    const updatedRemarks = [remark, ...userData.remarks];
    setUserData((prev) => ({ ...prev, remarks: updatedRemarks }));
    persistUpdate({ remarks: updatedRemarks });
  };

  const handleDeleteRemark = (id: string) => {
    setUserData((prev) => {
      const nextRemarks = prev.remarks.filter((r) => r.id !== id);
      persistUpdate({ remarks: nextRemarks });
      return { ...prev, remarks: nextRemarks };
    });
  };

  const handleAddObjective = (content: string) => {
    const objective: ClientObjective = {
      id: Date.now().toString(),
      content,
      timestamp: new Date(),
    };
    const updatedObjectives = [objective, ...userData.clientObjectives];
    setUserData((prev) => ({ ...prev, clientObjectives: updatedObjectives }));
    persistUpdate({ clientObjectives: updatedObjectives });
  };

  const handleDeleteObjective = (id: string) => {
    setUserData((prev) => {
      const nextObjectives = prev.clientObjectives.filter((o) => o.id !== id);
      persistUpdate({ clientObjectives: nextObjectives });
      return { ...prev, clientObjectives: nextObjectives };
    });
  };

  const handleAddPackage = (pkg: Omit<CoachingPackage, "id">) => {
    const newPkg: CoachingPackage = { ...pkg, id: Date.now().toString() };
    const updatedPackages = [...userData.coachingPackages, newPkg];
    setUserData((prev) => ({ ...prev, coachingPackages: updatedPackages }));
    persistUpdate({ coachingPackages: updatedPackages });
  };

  const handleDeletePackage = (id: string) => {
    setUserData((prev) => {
      const nextPackages = prev.coachingPackages.filter((p) => p.id !== id);
      persistUpdate({ coachingPackages: nextPackages });
      return { ...prev, coachingPackages: nextPackages };
    });
  };

  const handleToggleMethod = (method: Method) => {
    const key = method.id || method.name;
    if (!key) return;
    setUserData((prev) => {
      const has =
        prev.methodsUsed.includes(key) ||
        (method.name && prev.methodsUsed.includes(method.name));
      const filtered = prev.methodsUsed.filter(
        (m) => m !== key && m !== method.name,
      );
      const next = has ? filtered : [...filtered, key];
      persistUpdate({ methodsUsed: next });
      return { ...prev, methodsUsed: next };
    });
  };

  const handleSetAttributeValue = (
    attribute: string,
    value: boolean | null,
  ) => {
    setUserData((prev) => {
      const next = { ...prev.personalAttributes, [attribute]: value };
      persistUpdate({ personalAttributes: next });
      return { ...prev, personalAttributes: next };
    });
  };

  const handleAddAttribute = (attrName: string) => {
    const attr = attrName.trim();
    if (attr && !availableAttributes.includes(attr)) {
      const saveAttribute = async () => {
        try {
          const creatorEmail = (appContext.state.invoker?.email || "").trim();
          const newDetail: ExtendedClientDetailsEntity = {
            details: attr,
            creator: creatorEmail || undefined,
          };
          await extendedDetailsService.save(newDetail);
          setAvailableAttributes((prev) => [...prev, attr]);
          setUserData((prev) => ({
            ...prev,
            personalAttributes: { ...prev.personalAttributes, [attr]: null },
          }));
          setNewAttribute("");
        } catch (err) {
          console.error("Failed to save attribute", err);
          setError(t("coachingNavigator.clientOverview.failedToSaveAttribute"));
        }
      };
      saveAttribute();
    }
  };

  const handleDeleteAttribute = (attribute: string) => {
    setAvailableAttributes((prev) => prev.filter((a) => a !== attribute));
    setUserData((prev) => {
      const { [attribute]: _, ...rest } = prev.personalAttributes;
      persistUpdate({ personalAttributes: rest });
      return { ...prev, personalAttributes: rest };
    });
  };

  const handleStartEditAttribute = (attribute: string) => {
    setEditingAttribute(attribute);
    setEditAttributeValue(attribute);
  };

  const handleSaveEditAttribute = () => {
    if (
      editingAttribute &&
      editAttributeValue.trim() &&
      editAttributeValue.trim() !== editingAttribute
    ) {
      const nextName = editAttributeValue.trim();
      if (!availableAttributes.includes(nextName)) {
        setAvailableAttributes((prev) =>
          prev.map((a) => (a === editingAttribute ? nextName : a)),
        );
        setUserData((prev) => {
          const value = prev.personalAttributes[editingAttribute];
          const { [editingAttribute]: _, ...rest } = prev.personalAttributes;
          const next = { ...rest, [nextName]: value };
          persistUpdate({ personalAttributes: next });
          return { ...prev, personalAttributes: next };
        });
      }
    }
    setEditingAttribute(null);
    setEditAttributeValue("");
  };

  const handleAddInvoice = (invoiceData: Omit<Invoice, "id">) => {
    const invoice: Invoice = { ...invoiceData, id: Date.now().toString() };
    const updatedInvoices = [...userData.invoices, invoice];
    setUserData((prev) => ({ ...prev, invoices: updatedInvoices }));
    persistUpdate({ invoices: updatedInvoices });
  };

  const handleUpdateInvoice = (
    id: string,
    field: keyof Invoice,
    value: unknown,
  ) => {
    setUserData((prev) => {
      const updatedInvoices = prev.invoices.map((inv) =>
        inv.id === id ? { ...inv, [field]: value } : inv,
      );
      persistUpdate({ invoices: updatedInvoices });
      return { ...prev, invoices: updatedInvoices };
    });
  };

  const handleDeleteInvoice = (id: string) => {
    setUserData((prev) => {
      const nextInvoices = prev.invoices.filter((inv) => inv.id !== id);
      persistUpdate({ invoices: nextInvoices });
      return { ...prev, invoices: nextInvoices };
    });
  };

  const handleDelete = async () => {
    if (!coacheeId) return;
    setLoading(true);
    setError(null);
    try {
      await coacheeService.delete(coacheeId);
      setUserData(emptyUserData);
      setAvailableAttributes([]);
      setDeleteDialogOpen(false);
      navigate(-1);
    } catch (err) {
      console.error("Failed to delete coachee", err);
      setError(t("coachingNavigator.clientOverview.failedToDelete"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPurgeDialog = () => {
    setDeleteDialogOpen(false);
    setPurgeConfirmName("");
    setPurgeDialogOpen(true);
  };

  const handlePurgeConfirm = async () => {
    if (!coacheeId) return;
    setLoading(true);
    setError(null);
    try {
      const clientName = userData.name;
      await coacheeService.purge(coacheeId);
      const purgedData: UserData = { ...emptyUserData, name: clientName };
      setUserData(purgedData);
      await coacheeService.save({
        id: coacheeId,
        name: clientName,
        email: "",
        bookedHours: 0,
        completedHours: 0,
      } as Coachee);
      setAvailableAttributes([]);
      setPurgeDialogOpen(false);
      setPurgeConfirmName("");
    } catch (err) {
      console.error("Failed to purge coachee", err);
      setError(t("coachingNavigator.clientOverview.failedToDelete"));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    try {
      exportClientToPDF(userData, availableMethods, t);
      setExportAnchorEl(null);
    } catch (error) {
      console.error("Failed to export PDF", error);
      setError(t("coachingNavigator.clientOverview.failedToExportPDF"));
    }
  };

  const handleShowPDF = () => {
    try {
      exportClientToPDF(userData, availableMethods, t, "show");
      setExportAnchorEl(null);
    } catch (error) {
      console.error("Failed to show PDF", error);
      setError(t("coachingNavigator.clientOverview.failedToExportPDF"));
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 3 }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {loading && <SkeletonLoader variant="clientOverview" />}
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        {!loading && (
          <>
            {/* Action bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: 1,
                px: 3,
                py: 2,
              }}
            >
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: "text.secondary" }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>

              <Typography
                variant="h5"
                sx={{ fontWeight: 600, flexGrow: 1, textAlign: "center" }}
              >
                {t("coachingNavigator.clientOverview.title")}
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={(e) => setExportAnchorEl(e.currentTarget)}
                >
                  {t("coachingNavigator.clientOverview.export")}
                </Button>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={Boolean(exportAnchorEl)}
                  onClose={() => setExportAnchorEl(null)}
                >
                  <MenuItem onClick={handleExportPDF}>
                    <PdfIcon sx={{ mr: 1 }} fontSize="small" />
                    {t("coachingNavigator.clientOverview.exportPDF")}
                  </MenuItem>
                  <MenuItem onClick={handleShowPDF}>
                    <PdfIcon sx={{ mr: 1 }} fontSize="small" />
                    {t("coachingNavigator.clientOverview.showPDF")}
                  </MenuItem>
                </Menu>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  {t("coachingNavigator.clientOverview.delete")}
                </Button>
              </Box>
            </Box>

            {/* Key Information */}
            <KeyInformationSection
              userData={userData}
              onUpdate={handleKeyInfoUpdate}
              onWohnortChange={handleWohnortChange}
              onArbeitsortChange={handleArbeitsortChange}
              onSave={persistUpdate}
            />

            {/* Client Objectives */}
            <ClientObjectivesSection
              objectives={userData.clientObjectives}
              onAddObjective={handleAddObjective}
              onDeleteObjective={handleDeleteObjective}
            />

            {/* Further Remarks */}
            <FurtherRemarksSection
              remarks={userData.remarks}
              onAddRemark={handleAddRemark}
              onDeleteRemark={handleDeleteRemark}
            />

            {/* Methods & Personal Info */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <MethodsSection
                availableMethods={availableMethods}
                methodsUsed={userData.methodsUsed}
                onToggleMethod={handleToggleMethod}
              />
              <PersonalInformationSection
                personalAttributes={userData.personalAttributes}
                availableAttributes={availableAttributes}
                onSetAttributeValue={handleSetAttributeValue}
                onAddAttribute={handleAddAttribute}
                onDeleteAttribute={handleDeleteAttribute}
                onStartEditAttribute={handleStartEditAttribute}
                onSaveEditAttribute={handleSaveEditAttribute}
                editingAttribute={editingAttribute}
                editAttributeValue={editAttributeValue}
                onEditAttributeValueChange={setEditAttributeValue}
                onCancelEditAttribute={() => {
                  setEditingAttribute(null);
                  setEditAttributeValue("");
                }}
                newAttribute={newAttribute}
                onNewAttributeChange={setNewAttribute}
              />
            </Box>

            {/* Coaching Packages & Invoices */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <CoachingPackageSection
                packages={userData.coachingPackages}
                completedHours={userData.completedHours}
                onAddPackage={handleAddPackage}
                onDeletePackage={handleDeletePackage}
              />
              <AdminSection
                invoices={userData.invoices}
                onAddInvoice={handleAddInvoice}
                onUpdateInvoice={handleUpdateInvoice}
                onDeleteInvoice={handleDeleteInvoice}
              />
            </Box>
          </>
        )}

        {/* Delete Confirmation */}
        <ConfirmationPanel
          showModal={deleteDialogOpen}
          hideModal={() => setDeleteDialogOpen(false)}
          confirmModal={handleDelete}
          entry={{ email: userData.email, name: userData.name }}
          type="user"
          action="delete"
          title={t("coachingNavigator.clientOverview.deleteConfirmTitle")}
          content={t("coachingNavigator.clientOverview.deleteConfirmContent")}
          customActions={
            <>
              <Button
                variant="outlined"
                onClick={() => setDeleteDialogOpen(false)}
                sx={{ minHeight: 12, whiteSpace: "nowrap" }}
              >
                {t("coachingNavigator.clientOverview.cancel")}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleOpenPurgeDialog}
                sx={{ minHeight: 12, whiteSpace: "nowrap" }}
              >
                {t("coachingNavigator.clientOverview.purgeData")}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                sx={{ minHeight: 12, whiteSpace: "nowrap" }}
              >
                {t("coachingNavigator.clientOverview.deleteClient")}
              </Button>
            </>
          }
        />

        {/* Custom Purge Confirmation Dialog */}
        <Dialog
          open={purgeDialogOpen}
          onClose={() => {
            setPurgeDialogOpen(false);
            setPurgeConfirmName("");
          }}
          fullWidth
          maxWidth="xs" slotProps={{ paper: { sx: { backgroundColor: "white", borderRadius: 2 } } }}
        >
          <DialogTitle>
            {t("coachingNavigator.clientOverview.purgeData")}
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              {t("coachingNavigator.clientOverview.purgeConfirmBody_before")}
              <span style={{ fontWeight: 600 }}>{userData.name}</span>
              {t("coachingNavigator.clientOverview.purgeConfirmBody_after")}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={userData.name || ""}
              value={purgeConfirmName}
              onChange={(e) => setPurgeConfirmName(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPurgeDialogOpen(false);
                setPurgeConfirmName("");
              }}
            >
              {t("button.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={purgeConfirmName !== userData.name}
              onClick={handlePurgeConfirm}
            >
              {t("button.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default ClientOverview;
